/**
 * Validation middleware for StudioMcpBus.
 *
 * Intercepts MCP commands and validates entity references (elementId,
 * component_id, blockName, flowId, sourceId, targetId) against the
 * current editor state. Invalid references are rejected with structured
 * errors BEFORE the handler runs — preventing invalid commands from
 * polluting the undo stack or mutating state.
 *
 * Registration order: This middleware should be registered AFTER undo
 * middleware so that it sits outermost in the chain (last-added = outermost).
 * Validation runs first, and only valid commands proceed to undo recording.
 */

import type { Middleware, McpResult } from './types'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Recursively search for an element by id in a CogoElement tree. */
function findElementInTree(node: any, targetId: string): boolean {
  if (!node) return false
  if (node.id === targetId) return true
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (findElementInTree(child, targetId)) return true
    }
  }
  return false
}

/**
 * Resolve a block's element tree root. Blocks exist in two shapes:
 * v2 `BlockDef` ({ ui: { root } } — session-created/generated blocks) and
 * the flat `CogoBlockDef` alias ({ root } — server-loaded hybrid). The
 * nested `ui.root` is the live tree (immer copy-on-write diverges the flat
 * alias after the first edit), so prefer it.
 */
function blockRoot(block: unknown): unknown {
  const b = block as { ui?: { root?: unknown }; root?: unknown } | null | undefined
  return b?.ui?.root ?? b?.root
}

/** All block maps a command may legitimately reference. The MCP handlers
 * mutate `useStudioStore.currentProject.blocks`; the page editor mirrors
 * into `useEditorStore.blocks`. Either store may be the only one populated
 * (e.g. generated app without the page editor mounted). */
function candidateBlockMaps(): Array<Record<string, unknown>> {
  const maps: Array<Record<string, unknown>> = []
  const editorBlocks = useEditorStore.getState().blocks
  if (editorBlocks) maps.push(editorBlocks as Record<string, unknown>)
  const projectBlocks = useStudioStore.getState().currentProject?.blocks
  if (projectBlocks) maps.push(projectBlocks as Record<string, unknown>)
  return maps
}

/** Check if an element exists in any block's element tree. */
function elementExists(elementId: string): boolean {
  for (const blocks of candidateBlockMaps()) {
    for (const block of Object.values(blocks)) {
      if (findElementInTree(blockRoot(block), elementId)) {
        return true
      }
    }
  }
  return false
}

/** Check if a block exists by name. */
function blockExists(blockName: string): boolean {
  return candidateBlockMaps().some((blocks) => blockName in blocks)
}

/** Check if a flow exists by id. */
function flowExists(flowId: string): boolean {
  const { flows } = useFlowEditorStore.getState()
  return flows.some((f: any) => f.id === flowId)
}

/** Check if a node exists by id in the flow editor. */
function nodeExists(nodeId: string): boolean {
  const { nodes } = useFlowEditorStore.getState()
  return nodes.some((n: any) => n.id === nodeId)
}

// ---------------------------------------------------------------------------
// Middleware factory
// ---------------------------------------------------------------------------

/**
 * Create validation middleware for the MCP bus.
 *
 * Validates entity references in command params before allowing the
 * command to proceed to the handler. Returns structured errors for
 * invalid references without throwing.
 */
export function createValidationMiddleware(): Middleware {
  return async (cmd, next): Promise<McpResult> => {
    const { params } = cmd

    // ── Element references (elementId, component_id) ──
    const elementId = (params.elementId ?? params.component_id) as string | undefined
    if (elementId && !elementExists(elementId)) {
      return {
        success: false,
        error: `Element "${elementId}" not found in any block`,
      }
    }

    // ── Block references (blockName) ──
    const blockName = params.blockName as string | undefined
    if (blockName && !blockExists(blockName)) {
      return {
        success: false,
        error: `Block "${blockName}" not found`,
      }
    }

    // ── Flow references (flowId) ──
    const flowId = params.flowId as string | undefined
    if (flowId && !flowExists(flowId)) {
      return {
        success: false,
        error: `Flow "${flowId}" not found`,
      }
    }

    // ── Node references (sourceId, targetId) ──
    const sourceId = params.sourceId as string | undefined
    if (sourceId && !nodeExists(sourceId)) {
      return {
        success: false,
        error: `Node "${sourceId}" not found in flow`,
      }
    }

    const targetId = params.targetId as string | undefined
    if (targetId && !nodeExists(targetId)) {
      return {
        success: false,
        error: `Node "${targetId}" not found in flow`,
      }
    }

    // All references valid (or no references to validate) — proceed
    return next()
  }
}
